import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

/**
 * 토스 결제위젯 success/fail 리다이렉트 후 서버 승인 API 호출.
 */
const TossPaymentReturnPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const doneRef = useRef(false);
  const [message, setMessage] = useState("결제를 확인하는 중입니다…");

  useEffect(() => {
    if (doneRef.current) return;
    doneRef.current = true;

    const intent = searchParams.get("intent");
    const failed = searchParams.get("failed");
    const code = searchParams.get("code");
    const errMessage = searchParams.get("message");
    const paymentKey = searchParams.get("paymentKey");
    const orderId = searchParams.get("orderId");
    const amount = searchParams.get("amount");

    // 결제 실패 처리
    if (failed === "1" || code) {
      const msg = errMessage
        ? decodeURIComponent(errMessage)
        : "결제가 취소되었거나 실패했습니다.";
      navigate(`/nice-result?ok=0&message=${encodeURIComponent(msg)}`, {
        replace: true,
      });
      return;
    }

    // 결제 정보 검증
    if (!paymentKey || !orderId || amount == null) {
      navigate(
        `/nice-result?ok=0&message=${encodeURIComponent("결제 정보가 올바르지 않습니다.")}`,
        { replace: true },
      );
      return;
    }

    // 로그인 정보 검증
    const jwt = localStorage.getItem("jwt");
    if (!jwt) {
      navigate(
        `/nice-result?ok=0&message=${encodeURIComponent("로그인이 필요합니다.")}`,
        { replace: true },
      );
      return;
    }

    // 결제 금액 검증
    const apiBase = process.env.REACT_APP_API_URL ?? "";
    const numAmount = Number(amount);
    if (!Number.isFinite(numAmount)) {
      navigate(
        `/nice-result?ok=0&message=${encodeURIComponent("결제 금액이 올바르지 않습니다.")}`,
        { replace: true },
      );
      return;
    }

    // 결제 승인 처리
    void (async () => {
      try {
        let path = "";
        let body: Record<string, unknown> = {
          paymentKey,
          orderId,
          amount: numAmount,
        };

        if (intent === "live-order") {
          path = "/payments/toss/confirm-order";
        } else if (intent === "pack-today") {
          path = "/payments/toss/confirm-pack";
          body = { ...body, variant: "today" };
        } else if (intent === "pack-tomorrow") {
          path = "/payments/toss/confirm-pack";
          body = { ...body, variant: "tomorrow" };
        } else {
          navigate(
            `/nice-result?ok=0&message=${encodeURIComponent("결제 경로가 올바르지 않습니다.")}`,
            { replace: true },
          );
          return;
        }

        const res = await fetch(`${apiBase}${path}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(body),
        });

        // 결제 승인 결과 파싱
        let json: {
          ok?: boolean;
          amount?: number;
          orderId?: number;
          packId?: number;
          message?: string;
        } = {};
        try {
          json = (await res.json()) as typeof json;
        } catch {
          json = {};
        }

        // 결제 승인 결과 검증
        if (!res.ok || json.ok !== true) {
          const msg = json.message ?? "결제 승인에 실패했습니다.";
          navigate(`/nice-result?ok=0&message=${encodeURIComponent(msg)}`, {
            replace: true,
          });
          return;
        }

        // 결제 승인 결과 처리
        const okAmount = json.amount ?? numAmount;
        const oid = json.orderId;
        const packId = json.packId;
        const q = new URLSearchParams({
          ok: "1",
          amount: String(okAmount),
          orderId: String(oid),
        });
        if (packId != null) {
          q.set("packId", String(packId));
        }
        navigate(`/nice-result?${q.toString()}`, { replace: true });
      } catch {
        setMessage("네트워크 오류가 발생했습니다.");
        navigate(
          `/nice-result?ok=0&message=${encodeURIComponent("네트워크 오류가 발생했습니다.")}`,
          { replace: true },
        );
      }
    })();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4">
      <p className="text-gray-700">{message}</p>
    </div>
  );
};

export default TossPaymentReturnPage;
